import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IMPORT_STATE } from '@/entities/(integration)/integrations';
import { toast } from '@/stores/pure/ToastStore';

import type { QualtricsInfo } from '../model';
import { createQualtricsInfo } from './create-qualtrics-info';
import { deleteQualtricsInfo } from './delete-qualtrics-info';
import type { getQualtricsInfo } from './get-qualtrics-info';
import { importQualtricsFeedback } from './import-qualtrics-feedback';
import { qualtricsQueryKeys } from './qualtrics.queries';

/**
 * Qualtrics API Key 기반 credential 생성 mutation
 */
export const useCreateQualtricsMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { api_key: string; data_center_id: string }) =>
      createQualtricsInfo({
        workspace_id: workspaceAlias,
        ...params,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qualtricsQueryKeys.all(workspaceAlias),
      });
      toast('Qualtrics connected successfully.', 'success');
    },
  });
};

/**
 * Qualtrics 연동 해제 mutation
 */
export const useDeleteQualtricsMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteQualtricsInfo({ workspace_id: workspaceAlias }),
    onSuccess: () => {
      const emptyData: Awaited<ReturnType<typeof getQualtricsInfo>> = [];
      queryClient.setQueryData(
        qualtricsQueryKeys.all(workspaceAlias),
        () => emptyData,
      );

      toast('Qualtrics disconnected successfully.', 'success');
    },
  });
};

/**
 * Qualtrics 피드백 가져오기 mutation (optimistic update 포함)
 */
export const useImportQualtricsFeedbackMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dateRange }: { dateRange: NullableDateRangeT }) =>
      importQualtricsFeedback({
        workspace_id: workspaceAlias,
        start_date: dateRange.start_date || '',
        end_date: dateRange.end_date || '',
      }),
    onSuccess: () => {
      // optimistic update: 단일 항목이므로 배열 전체를 업데이트
      queryClient.setQueryData(
        qualtricsQueryKeys.all(workspaceAlias),
        (old: QualtricsInfo[] | undefined) => {
          if (!old) return old;
          return old.map((w) => ({
            ...w,
            import_state: IMPORT_STATE.IMPORTING,
          }));
        },
      );
    },
  });
};
